import type { Step as RcStep } from '@sb/remote-control/types';

import { type ValidationRule } from '../../types';
import { ActionRequiredError, type WarningFinding } from '../../util/errors';
import { validateCodeExpression } from '../../util/validators';

import { CanonicalStepRuleBase } from './base';
import type { CanonicalValidateArgs } from './base';

class SetEnvironmentVariableRule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.SetEnvironmentVariable', 'SetEnvironmentVariable');
  }

  protected validate({ args, warn }: CanonicalValidateArgs): void {
    type SEVArgs = Extract<
      RcStep.Arguments,
      { argumentKind: 'SetEnvironmentVariable' }
    >;
    const a = args as SEVArgs;
    warn(this.checkExpression(a));
    this.checkVariableId(a);
    this.checkNewValue(a);
  }

  private checkExpression(
    args: Extract<RcStep.Arguments, { argumentKind: 'SetEnvironmentVariable' }>,
  ): WarningFinding | undefined {
    /**
     * Validate newValue expression for the environment variable. Invalid
     * syntax throws; deprecated syntax is recorded via `warn`.
     *
     * @throws {ActionRequiredError}
     */
    return validateCodeExpression(args.newValue, 'newValue', 'New value');
  }

  private checkVariableId(
    args: Extract<RcStep.Arguments, { argumentKind: 'SetEnvironmentVariable' }>,
  ): void {
    /**
     * Ensure variableID is provided.
     * Throws ActionRequiredError if missing.
     *
     * @throws {ActionRequiredError}
     */
    if (!args.variableID) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Variable ID required',
        fieldId: 'variableID',
      });
    }
  }

  private checkNewValue(
    args: Extract<RcStep.Arguments, { argumentKind: 'SetEnvironmentVariable' }>,
  ): void {
    /**
     * Ensure newValue is provided.
     * Throws ActionRequiredError if missing.
     *
     * @throws {ActionRequiredError}
     */
    if (!args.newValue) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Value is required',
        fieldId: 'newValue',
      });
    }
  }
}

export const SetEnvironmentVariableValidation: ValidationRule =
  new SetEnvironmentVariableRule().toValidationRule();
